import React from 'react';
import { PixelRatio, View } from 'react-native';
import { Button } from 'app/design/primitives/button';
import { Trans } from 'react-i18next';
import { useScopedTranslation } from 'app/hooks/i18n';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import TutoCircle from 'app/design/images/tuto-circle';
import type { NavigationScreen } from 'app/types/navigation';
import { withErrorBoundary } from 'app/components/error-boundary';
import Constants from 'expo-constants';
import { useNavigation } from 'app/hooks/navigation';
import TutoPublishOffers from 'app/design/images/tuto-publish-offers';
import { Tutorial } from 'app/types/tutorial';
import { useTutorials } from 'app/components/tutorial-overlay';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

const TutorialModalScreenComponent: NavigationScreen<'tutorialModal'> = ({ route }) => {
  const { t } = useScopedTranslation();
  const navigation = useNavigation<'tutorialModal'>();

  const tutorial = route.params.tutorial;

  const bgColor = {
    [Tutorial.ChatCreation]: '#FFFFFF',
    [Tutorial.FirstConversation]: '#FFFFFF',
    [Tutorial.FirstMessageSent]: '#FFFFFF',
    [Tutorial.MultipleOffers]: '#FFFFFF',
    [Tutorial.PublishOffers]: '#CFC1E1',
    [Tutorial.Conciergerie]: '#CFC1E1',
    [Tutorial.HomeExplore]: '#FFFFFF',
    [Tutorial.ContactMember]: '#FFFFFF',
    [Tutorial.ChatIntro]: '#FFFFFF',
  };

  const Illustration = {
    [Tutorial.ChatCreation]: TutoPublishOffers,
    [Tutorial.FirstConversation]: TutoPublishOffers,
    [Tutorial.FirstMessageSent]: TutoPublishOffers,
    [Tutorial.MultipleOffers]: TutoPublishOffers,
    [Tutorial.PublishOffers]: TutoPublishOffers,
    [Tutorial.Conciergerie]: TutoPublishOffers,
    [Tutorial.HomeExplore]: TutoPublishOffers,
    [Tutorial.ContactMember]: TutoPublishOffers,
    [Tutorial.ChatIntro]: TutoPublishOffers,
  };

  const backgroundColor = bgColor[tutorial];
  const TutorialIllustration = Illustration[tutorial];

  // fontSize / margin / padding were designed for pixelRatio = 3; for smaller ratio, we need to adjust those values to be smaller,
  //   otherwise, we'll have giant fonts on devices with a low pixel ratio.
  const layoutIsForRatio = 3;
  // Tablets have more than enough space; don't resize-down all the measurements on tablets
  const isTablet = useDeviceWidth() > 550;
  const pixelRatioScale = isTablet ? 1 : Math.max(0.7, PixelRatio.get() / layoutIsForRatio);

  const { continueTutorial, stopTutorial } = useTutorials();

  const closeScreen = () => {
    stopTutorial(false);
    navigation.goBack();
  };

  const pressContinue = () => {
    navigation.goBack();
    void continueTutorial();
  };

  return (
    <Column
      style={{
        paddingTop: Constants.statusBarHeight,
        backgroundColor,
        width: useDeviceWidth(),
        height: '100%',
      }}
    >
      <Row alignItems="center" style={{ height: 50 }}>
        <Button flex={1} onPress={closeScreen} style={{ maxWidth: 50 }}>
          <IconX strokeWidth={ICON_STROKE_WIDTH} />
        </Button>
      </Row>

      <Text
        flex={0}
        style={{
          fontSize: 30,
          fontWeight: '700',
          paddingHorizontal: 55 * pixelRatioScale,
          textAlign: 'center',
          width: '100%',
        }}
        variant="bodyBase"
      >
        {t(`${tutorial}.title`)}
      </Text>

      <Row style={{ flex: 1, marginTop: 25 * pixelRatioScale, alignItems: 'center', alignSelf: 'center' }}>
        <View style={{ boxShadow: '0px 10px 18px rgba(0, 0, 0, 0.25)' }}>
          <TutorialIllustration />
        </View>
      </Row>

      <View
        style={{
          flex: 0,
          marginHorizontal: 15 * pixelRatioScale,
          marginVertical: 20 * pixelRatioScale,
          marginTop: 75 * pixelRatioScale,
          borderRadius: 32 * pixelRatioScale,
          backgroundColor: '#ffffff',
          paddingTop: 35 * pixelRatioScale,
          paddingHorizontal: 25 * pixelRatioScale,
          paddingBottom: 25 * pixelRatioScale,
        }}
      >
        <View
          style={{
            marginTop: -90 * pixelRatioScale,
            marginHorizontal: 'auto',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <TutoCircle size={95 * pixelRatioScale} />
        </View>
        <Text
          style={{
            fontSize: 18 * pixelRatioScale,
            marginBottom: 25 * pixelRatioScale,
            paddingHorizontal: 50 * pixelRatioScale,
            textAlign: 'center',
          }}
        >
          <Trans components={{ strong: <Text fontWeight="700" /> }} i18nKey={`${tutorial}.text`} ns="tutorialModal" />
        </Text>
        <Button onPress={pressContinue} variant="primary">
          {t(`${tutorial}.ctaButton`)}
        </Button>
      </View>
    </Column>
  );
};

export const TutorialModalScreen: NavigationScreen<'tutorialModal'> = withErrorBoundary(TutorialModalScreenComponent);
